import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import { JOIN_DEFAULTS, joinLines } from '../../api/client'
import type { JoinInputLine } from '../../api/client'
import { describeError } from '../../api/errors'
import type { JoinMode, JoinOptions, JoinReport } from '../../api/types'
import type { AnalysisDraft, DraftLine } from '../../analysis/draft'

export type JoinPanelProps = {
  draft: AnalysisDraft
  disabled: boolean
  onChange: (next: AnalysisDraft) => void
}

const REASONS: Record<string, string> = {
  gap: 'короткая пауза',
  nonverbal: 'звук без слов внутри фразы',
  unfinished: 'фраза не закончена',
  continuation: 'продолжение со строчной буквы',
  repeat: 'повтор',
}

/**
 * Склейка раздробленных реплик (docs/BACKLOG.md, задача 1).
 *
 * Предпросмотр считается на сервере от текущих строк черновика и ничего не
 * меняет, пока не нажата кнопка. Подбирать порог повторным разбором было бы
 * издевательством: он занимает минуты работы видеокарты, а склейка — чистая
 * функция, и сервис умеет вызывать её отдельно.
 */
export function JoinPanel({ draft, disabled, onChange }: JoinPanelProps): JSX.Element {
  const [params, setParams] = useState<JoinOptions>(JOIN_DEFAULTS)
  const [report, setReport] = useState<JoinReport | null>(null)
  const [preview, setPreview] = useState<DraftLine[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const requestId = useRef(0)

  const lines = draft.lines

  useEffect(() => {
    if (params.mode === 'off' || lines.length < 2) {
      setPreview(null)
      setReport(null)
      setError(null)
      return
    }
    const id = ++requestId.current
    setBusy(true)
    // Пауза перед запросом: движение ползунка не должно порождать запрос на кадр.
    const timer = window.setTimeout(() => {
      void joinLines(lines.map(toJoinLine), params)
        .then((res) => {
          if (id !== requestId.current) return
          setPreview(res.lines.map((line) => fromJoinLine(line, lines)))
          setReport(res.join)
          setError(null)
        })
        .catch((err: unknown) => {
          if (id !== requestId.current) return
          setError(describeError(err))
          setPreview(null)
          setReport(null)
        })
        .finally(() => {
          if (id === requestId.current) setBusy(false)
        })
    }, 250)
    return () => {
      window.clearTimeout(timer)
    }
  }, [lines, params])

  const reasons = new Map<string, number>()
  for (const merge of report?.merges ?? []) {
    reasons.set(merge.reason, (reasons.get(merge.reason) ?? 0) + 1)
  }

  return (
    <section className="join-panel">
      <h3>Склейка раздробленных реплик</h3>
      <p className="faint">
        Границы строит детектор голоса, и пауза внутри фразы для него неотличима от паузы
        между фразами: «Нет… Нет… Этого не может быть» приезжает тремя строками.
      </p>

      <div className="row wrap join-controls">
        <label>
          <span>Режим</span>
          <select
            value={params.mode}
            disabled={disabled}
            onChange={(e) => setParams((p) => ({ ...p, mode: e.target.value as JoinMode }))}
          >
            <option value="off">не склеивать</option>
            <option value="manual">по паузе</option>
            <option value="auto">автоматически</option>
          </select>
        </label>

        {params.mode === 'manual' ? (
          <label>
            <span>Максимальная пауза: {params.maxGap.toFixed(2)} с</span>
            <input
              type="range"
              min={0.1}
              max={2}
              step={0.05}
              value={params.maxGap}
              disabled={disabled}
              onChange={(e) => setParams((p) => ({ ...p, maxGap: Number(e.target.value) }))}
            />
          </label>
        ) : null}

        {params.mode !== 'off' ? (
          <label>
            <span>Предел длины реплики: {params.maxDuration.toFixed(0)} с</span>
            <input
              type="range"
              min={3}
              max={30}
              step={1}
              value={params.maxDuration}
              disabled={disabled}
              onChange={(e) => setParams((p) => ({ ...p, maxDuration: Number(e.target.value) }))}
            />
          </label>
        ) : null}
      </div>

      {params.mode === 'auto' ? (
        <p className="faint">
          Порог подбирается по самой записи
          {report ? ` — получилось ${report.maxGap.toFixed(2)} с` : ''}: у быстрого диалога
          «длинная» пауза короче, чем «короткая» у неторопливого. Плюс учитываются
          оборванные фразы и повторы вроде «Нет… Нет…».
        </p>
      ) : null}

      {error ? <p className="error-text">{error}</p> : null}

      {report ? (
        <>
          <p className={busy ? 'faint' : undefined}>
            <strong>
              {report.before} → {report.after}
            </strong>{' '}
            строк, склеек {report.merged}
            {busy ? ' · пересчитываю…' : ''}
          </p>
          {reasons.size > 0 ? (
            <ul className="join-reasons">
              {[...reasons.entries()]
                .sort((a, b) => b[1] - a[1])
                .map(([reason, count]) => (
                  <li key={reason}>
                    {REASONS[reason] ?? reason} — {count}
                  </li>
                ))}
            </ul>
          ) : null}
          <button
            type="button"
            className="primary"
            disabled={disabled || busy || !preview || report.merged === 0}
            onClick={() => {
              if (preview) onChange({ ...draft, lines: preview })
            }}
          >
            Применить склейку ({report.merged})
          </button>
        </>
      ) : null}
    </section>
  )
}

function toJoinLine(line: DraftLine): JoinInputLine {
  return {
    start: line.start,
    end: line.end,
    speaker: line.speaker || null,
    kind: line.kind,
    textOriginal: line.textOriginal,
    text: line.text,
    confidence: line.confidence ?? 0,
  }
}

/**
 * Собирает строку черновика обратно. Ключ и исходную метку диаризации берём у
 * первой из склеенных — она узнаётся по времени начала, потому что склейка
 * сохраняет начало первого куска.
 */
function fromJoinLine(line: JoinInputLine, source: DraftLine[]): DraftLine {
  const origin = source.find((l) => Math.abs(l.start - line.start) < 0.001) ?? source[0]
  return {
    key: origin?.key ?? `join-${line.start.toFixed(3)}`,
    start: line.start,
    end: line.end,
    speaker: line.speaker ?? '',
    speakerRaw: origin?.speakerRaw ?? '',
    text: line.text,
    textOriginal: line.textOriginal,
    confidence: line.confidence > 0 ? line.confidence : null,
    kind: line.kind === 'nonverbal' ? 'nonverbal' : 'speech',
  }
}
